<template>
  <PageShell :background="bg" wide>
    <PageHeader
      size="page"
      align="start"
      :title="t('playerCheatSheet.title')"
      :lead="t('playerCheatSheet.subtitle')"
    />

    <IndexedLayout :items="sections" :label="t('playerCheatSheet.sections.label')">
      <PageSection id="turn-cycle" :title="t('playerCheatSheet.turnCycleFlow.title')">
        <StepList :steps="turnCycleSteps" />
      </PageSection>

      <PageSection id="action-types" :title="t('playerCheatSheet.actionTypes.title')">
        <Card>
          <DefinitionList :entries="actionTypes">
            <template #description="{ entry }">
              <Prose>
                <p>{{ entry.description }}</p>
              </Prose>
              <ul class="cs-tags cs-tags--after-prose">
                <Pill v-for="example in entry.examples" :key="example" as="li" :tone="entry.key">
                  {{ example }}
                </Pill>
              </ul>
            </template>
          </DefinitionList>
        </Card>
      </PageSection>

      <PageSection
        id="resources"
        :title="t('playerCheatSheet.resourceManagement.title')"
        :lead="t('playerCheatSheet.resourceManagement.lead')"
      >
        <Card>
          <DefinitionList :entries="resources" />
        </Card>
      </PageSection>

      <PageSection id="gm-interaction" :title="t('playerCheatSheet.playerGMInteraction.title')">
        <StepList :steps="interactionSteps" />
      </PageSection>

      <div class="cs-pair">
        <PageSection id="quick-actions" :title="t('playerCheatSheet.quickActions.title')">
          <ul class="cs-tags">
            <Pill v-for="action in quickActions" :key="action" as="li">{{ action }}</Pill>
          </ul>
        </PageSection>

        <PageSection id="key-rules" :title="t('playerCheatSheet.keyRules.title')">
          <Prose>
            <ul>
              <li v-for="rule in keyRules" :key="rule">{{ rule }}</li>
            </ul>
          </Prose>
        </PageSection>
      </div>

      <PageSection :title="t('playerCheatSheet.footer.title')">
        <Prose>
          <p>{{ t('playerCheatSheet.footer.readyToBegin') }}</p>
          <p>
            <a href="https://discord.gg/vC3TvVzK" target="_blank" rel="noopener noreferrer">
              {{ t('playerCheatSheet.footer.joinDiscord') }}
            </a>
          </p>
        </Prose>
        <LandingButton class="cs-cta" to="/faq/what-is-it" variant="ghost">
          {{ t('playerCheatSheet.footer.newcomersGuide') }}
        </LandingButton>
      </PageSection>
    </IndexedLayout>

    <ClosingCta />
  </PageShell>
</template>

<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import PageShell from '@/components/public/PageShell.vue';
import PageHeader from '@/components/public/PageHeader.vue';
import PageSection from '@/components/public/PageSection.vue';
import IndexedLayout from '@/components/public/IndexedLayout.vue';
import Prose from '@/components/public/Prose.vue';
import Card from '@/components/public/Card.vue';
import StepList from '@/components/public/StepList.vue';
import DefinitionList from '@/components/public/DefinitionList.vue';
import Pill from '@/components/public/Pill.vue';
import LandingButton from '@/components/btn/LandingButton.vue';
import ClosingCta from '@/components/public/ClosingCta.vue';
import bg from '@/assets/images/backgrounds/common/wasteland-a.webp';

const { t, tm, rt } = useI18n();

const list = (key) => {
  const raw = tm(key);
  return Array.isArray(raw)
    ? raw.map((entry) => (typeof entry === 'string' ? entry : rt(entry)))
    : [];
};

const steps = (base, count) =>
  Array.from({ length: count }, (_, index) => ({
    title: t(`${base}.step${index + 1}.title`),
    text: t(`${base}.step${index + 1}.description`),
  }));

const turnCycleSteps = computed(() => steps('playerCheatSheet.turnCycleFlow.steps', 5));
const interactionSteps = computed(() => steps('playerCheatSheet.playerGMInteraction.steps', 4));

/* The type name stays a mono uppercase term beside the hue-coded example
 * pills — the colour is never the only channel carrying the distinction. */
const actionTypes = computed(() =>
  ['immediate', 'scheduled', 'finite'].map((key) => ({
    key,
    term: t(`playerCheatSheet.actionTypes.${key}.title`),
    description: t(`playerCheatSheet.actionTypes.${key}.description`),
    examples: list(`playerCheatSheet.actionTypes.${key}.examples`),
  })),
);

const resources = computed(() =>
  ['actionPoints', 'energyFlow', 'healthPoints'].map((key) => ({
    term: t(`playerCheatSheet.resourceManagement.${key}.title`),
    description: t(`playerCheatSheet.resourceManagement.${key}.description`),
  })),
);

const quickActions = computed(() =>
  [
    'move',
    'tacticalMove',
    'useSkill',
    'useItem',
    'inspect',
    'snatch',
    'bargain',
    'openChest',
    'longRest',
    'backToSafety',
    'breakthrough',
    'diceRoll',
  ].map((key) => t(`playerCheatSheet.quickActions.actions.${key}`)),
);

const keyRules = computed(() =>
  ['rule1', 'rule2', 'rule3', 'rule4'].map((key) => t(`playerCheatSheet.keyRules.rules.${key}`)),
);

const sections = computed(() => [
  { id: 'turn-cycle', label: t('playerCheatSheet.turnCycleFlow.title') },
  { id: 'action-types', label: t('playerCheatSheet.actionTypes.title') },
  { id: 'resources', label: t('playerCheatSheet.resourceManagement.title') },
  { id: 'gm-interaction', label: t('playerCheatSheet.playerGMInteraction.title') },
  { id: 'quick-actions', label: t('playerCheatSheet.quickActions.title') },
  { id: 'key-rules', label: t('playerCheatSheet.keyRules.title') },
]);
</script>

<style scoped>
/* Two short reference blocks — eight one-word actions and four one-line rules
 * — that each occupied a full row of an already thin ribbon. This is
 * reference material; density is the point. */
.cs-pair {
  display: grid;
  gap: var(--space-8);
}

.cs-pair > * {
  scroll-margin-top: var(--space-6);
}

@media (min-width: 48rem) {
  .cs-pair {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    align-items: start;
  }
}

.cs-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  list-style: none;
  margin: 0;
  padding: 0;
}

.cs-tags > li {
  margin: 0;
}

.cs-tags--after-prose {
  margin-top: var(--space-3);
}

/* The section body is a column flexbox, so the closing link stretched to the
 * full content width and read as a banner rather than a button. */
.cs-cta {
  align-self: flex-start;
}
</style>
